import { isAnyOf, isFulfilled, isRejectedWithValue } from '@reduxjs/toolkit';
import type { ThunkDispatch, TypedStartListening } from '@reduxjs/toolkit';
import { setAccessToken } from '@skynet/data-auth-api';
import { signupApiEndpoints } from './signup-api';
import { EMAIL_CONFIRMATION_ERROR_MESSAGES, EMAIL_CONFIRMATION_REFRESH_KEY } from './errors';

/**
 * Extracts the server `message` from a rejected RTK Query payload.
 * `fetchBaseQuery` nests the response body under `data`: `{ status, data: { message } }`.
 */
function getErrorMessage(payload: unknown): string | undefined {
  if (typeof payload !== 'object' || payload === null || !('data' in payload)) return undefined;
  const { data } = payload;
  if (typeof data !== 'object' || data === null || !('message' in data)) return undefined;
  return typeof data.message === 'string' ? data.message : undefined;
}

function getAccessToken(payload: unknown): string | undefined {
  if (typeof payload === 'object' && payload !== null && 'accessToken' in payload) {
    const token = String((payload as Record<string, unknown>).accessToken);
    return typeof (payload as Record<string, unknown>).accessToken === 'string' ? token : undefined;
  }
  return undefined;
}

/**
 * Registers signup-related RTK listeners.
 * Call this once from the app's listener middleware setup.
 *
 * @param startListening - Typed `startAppListening` from your listener middleware
 */
export const setupSignupListeners = <S, D extends ThunkDispatch<any, any, any>, E>(
  startListening: TypedStartListening<S, D, E>,
): void => {
  // Refresh the accessToken once the email is confirmed, so its `isEmailConfirmed`
  // claim lets the signup flow move on to 2FA. The refresh is not unwrapped: a
  // failure is surfaced (and retried) by the confirmation page, which observes
  // it through `EMAIL_CONFIRMATION_REFRESH_KEY`.
  const refreshAfterConfirmation = async (dispatch: D) => {
    await dispatch(
      signupApiEndpoints.refreshToken.initiate(undefined, {
        fixedCacheKey: EMAIL_CONFIRMATION_REFRESH_KEY,
      }),
    );
  };

  startListening({
    matcher: signupApiEndpoints.confirmEmail.matchFulfilled,
    effect: async (_action, listenerApi) => {
      await refreshAfterConfirmation(listenerApi.dispatch);
    },
  });

  // The email was confirmed elsewhere (another tab or device).
  startListening({
    matcher: isAnyOf(
      signupApiEndpoints.confirmEmail.matchRejected,
      signupApiEndpoints.resendConfirmationEmail.matchRejected,
    ),
    effect: async (action, listenerApi) => {
      if (
        isRejectedWithValue(action) &&
        getErrorMessage(action.payload) === EMAIL_CONFIRMATION_ERROR_MESSAGES.alreadyConfirmed
      ) {
        await refreshAfterConfirmation(listenerApi.dispatch);
      }
    },
  });

  // Update authToken (single source of truth) when auth endpoints respond with accessToken
  startListening({
    matcher: isAnyOf(
      signupApiEndpoints.register.matchFulfilled,
      signupApiEndpoints.refreshToken.matchFulfilled,
      signupApiEndpoints.verifyMfa.matchFulfilled,
      signupApiEndpoints.login.matchFulfilled,
      signupApiEndpoints.authenticateMfa.matchFulfilled,
      signupApiEndpoints.resetMfa.matchFulfilled,
      signupApiEndpoints.recoverMfa.matchFulfilled,
    ),
    effect: (action, listenerApi) => {
      if (isFulfilled(action)) {
        const accessToken = getAccessToken(action.payload);
        if (accessToken) {
          listenerApi.dispatch(setAccessToken({ accessToken }));
        }
      }
    },
  });

  // Clear authToken on logout
  startListening({
    matcher: isAnyOf(signupApiEndpoints.logout.matchFulfilled),
    effect: (_action, listenerApi) => {
      listenerApi.dispatch(setAccessToken({ accessToken: undefined }));
    },
  });
};
